import { useApolloClient } from '@apollo/client'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import { useState } from 'react'
import DialogPauseProject from '../Project/PauseProject/DialogPauseProject'
import { usePauseResumeSurvey } from './usePauseResumeSurvey'

interface Props {
  isOpen: boolean
  toggleIsOpen: (open: boolean) => void
  surveyId: string
  // Drives both the wording and the action: paused → unpause, anything else →
  // pause. Keyed off "paused" (not "live") so it can't contradict the menu
  // item that opened the dialog, which offers "Pause" for every non-paused
  // state.
  isPaused: boolean
}

/**
 * Pause / unpause one market of a multi-market project. `PauseProject` is
 * hard-wired to `selectedProject.surveys[0]` (fine for single-market projects,
 * the wrong survey here), so this pauses the given market's survey instead and
 * refetches the queries that display its state.
 */
const PauseMarket = ({ isOpen, toggleIsOpen, surveyId, isPaused }: Props) => {
  const client = useApolloClient()
  const { enqueueSnackbar } = useSnackbar()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { pause, resume } = usePauseResumeSurvey(() =>
    client
      .refetchQueries({
        include: ['projectBySurveyId', 'projectById', 'surveyOrder']
      })
      // Refreshing the cache is best-effort: `surveyOrder` rejects for surveys
      // that never had an order placed.
      .catch(() => undefined)
  )

  // The dialog stays open (button busy) until the mutations settle, then
  // failures surface as a snackbar — closing immediately would drop them
  // silently.
  const handleConfirm = async () => {
    setIsSubmitting(true)
    const succeeded = await (isPaused ? resume(surveyId) : pause(surveyId))
    setIsSubmitting(false)
    toggleIsOpen(false)
    if (!succeeded) {
      enqueueSnackbar(
        `Could not ${
          isPaused ? 'unpause' : 'pause'
        } the market. Please try again.`,
        { variant: SnackbarVariant.Error }
      )
    }
  }

  return (
    <DialogPauseProject
      entity="market"
      isOpen={isOpen}
      isLive={!isPaused}
      isSubmitting={isSubmitting}
      toggleOpenDialog={toggleIsOpen}
      onPauseProject={() => {
        void handleConfirm()
      }}
    />
  )
}

export default PauseMarket
